import { shallowEqual } from 'react-redux';

import { Box, Heading, Modal, Paragraph } from '@zillow/constellation-10';
import { ContentStyledComponent } from '@zg-rentals/backend-styled-components';

import { useSelector } from '../../selectors/useSelector';

import Add3DToursCSEvents from '../../model/ClickstreamEvent/Add3DToursCSEvents';

import { RecsLearnMoreConstants } from '../../constants/RecommendationsConstants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';

import type { ReactNode } from 'react';

const OUR_APP = 'our app';
const HIRE_A_PHOTOGRAPHER = 'hire a photographer';

const CS_EVENTS_ID_MAP = {
  [OUR_APP]: Add3DToursCSEvents.PostingPathOurApp.data_event_id,
  [HIRE_A_PHOTOGRAPHER]: Add3DToursCSEvents.PostingPathHirePhotographer.data_event_id,
};

type Props = {
  onClose: () => void;
  footer?: ReactNode;
  recommendationType: keyof typeof RecsLearnMoreConstants;
  isFromPremiumAnalyticsPage?: boolean;
  isFromRelistingFlow?: boolean;
};

const RecommendationsLearnMoreModal = ({
  onClose,
  recommendationType,
  footer,
  isFromPremiumAnalyticsPage = false,
  isFromRelistingFlow = false,
}: Props) => {
  const {
    currentProperty,
    properties: {
      recommendations: { recommendationsByType },
      currentProperty: {
        freemium: {
          activeProduct: { type: activeProductType },
        },
      },
    },
    user,
  } = useSelector(
    (state) => ({
      properties: state.properties,
      currentProperty: state.properties.currentProperty,
      user: state.user,
    }),
    shallowEqual,
  );

  const recommendationFromApi = recommendationsByType[recommendationType];
  const isActiveProductPremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;
  const usePremiumData = isActiveProductPremium && RecsLearnMoreConstants[recommendationType]?.premium?.heading;
  const outcome = recommendationFromApi?.outcome;
  const heading = usePremiumData
    ? RecsLearnMoreConstants[recommendationType]?.premium?.heading
    : RecsLearnMoreConstants[recommendationType].heading;
  const hasOutcomeStats = outcome && recommendationFromApi?.outcomeStats;
  const source = recommendationFromApi?.source?.card;
  const isOutcomeDuplicateOfHeading = outcome === heading;

  const getBodyText = () => {
    if (recommendationFromApi?.learnMore) {
      return (
        <ContentStyledComponent
          title={recommendationFromApi?.learnMore?.title}
          content={recommendationFromApi?.learnMore?.content}
          csEventsHandler={
            recommendationFromApi?.learnMore?.title?.text === 'Add a 3D tour'
              ? ({ text }: { text: string }) => {
                  if (text === OUR_APP) {
                    Add3DToursCSEvents.PostingPathOurApp({ currentProperty, user });
                    return;
                  }

                  if (text === HIRE_A_PHOTOGRAPHER) {
                    Add3DToursCSEvents.PostingPathHirePhotographer({ currentProperty, user });
                    return;
                  }
                }
              : undefined
          }
          csEventsIdMap={CS_EVENTS_ID_MAP}
        />
      );
    } else if (usePremiumData && recommendationType) {
      return RecsLearnMoreConstants[recommendationType]?.premium?.body;
    } else {
      return RecsLearnMoreConstants[recommendationType].body;
    }
  };

  return (
    <Modal
      size={{
        base: 'fullScreen',
        lg: 'md',
      }}
      open={true}
      onOpenChange={(open) => !open && onClose()}
      body={
        <Box css={{ marginBottom: '600' }}>
          {!recommendationFromApi?.learnMore && (
            <>
              <Heading level={4} css={{ marginBottom: '400' }}>
                {heading}
              </Heading>
              {Boolean(
                usePremiumData &&
                outcome &&
                !isFromPremiumAnalyticsPage &&
                !isFromRelistingFlow &&
                !isOutcomeDuplicateOfHeading,
              ) && (
                <Paragraph
                  css={{ marginBlock: '200' }}
                >{`${recommendationsByType[recommendationType]?.outcome}.`}</Paragraph>
              )}
            </>
          )}
          <Box css={{ marginBlock: '200' }}>{getBodyText()}</Box>
          {!recommendationFromApi?.learnMore && Boolean(hasOutcomeStats && source && !isFromPremiumAnalyticsPage) && (
            <Paragraph textStyle="body-xs" color="text.subtle" css={{ marginBlock: '200' }}>
              {source}
            </Paragraph>
          )}
        </Box>
      }
      footer={footer}
    ></Modal>
  );
};

export default RecommendationsLearnMoreModal;
